// Full content for every industry-specific (trade) landing page.
// Keyed by slug, e.g. "hvac-houston-tx". All data is FRONTEND MOCK.

export const TRADE_CITIES =
  "Houston · Kemah · Clear Lake · League City · Webster · Friendswood · Pasadena · La Porte · Baytown — anywhere inside 20 miles.";

// Generic "The stack, applied to your trade" copy. {t} = lowercase trade label.
export const buildStack = (t) => [
  {
    title: "Website — free build",
    body: `A fast, mobile-first ${t} site tuned for coastal Texas homeowners. Real photos of your work. Real copy. Trust earned on sight.`,
  },
  {
    title: "AI phone assistant",
    body: `Answers ${t} calls when you're on a job. Qualifies the lead. Books the appointment. Never sleeps, never quits mid-day.`,
  },
  {
    title: "Missed-call text back",
    body: `You miss a call — they get a text in 3 seconds. Most say yes right there, before they ever call the next contractor.`,
  },
  {
    title: "Review automation",
    body: `Every finished ${t} job asks the customer for a Google review the same day. That's what makes the next homeowner in Kemah or League City call you.`,
  },
  {
    title: "CRM & calendar",
    body: `Every lead in one place. Customers book directly. No slips through the cracks, no double-booked crews on the same street.`,
  },
  {
    title: "Follow-up automations",
    body: `Old ${t} estimates auto-follow-up. Seasonal reminders. Runs while you're on the job.`,
  },
];

export const TRADE_PAGES = {
  "hvac-houston-tx": {
    name: "HVAC",
    label: "hvac",
    icon: "Snowflake",
    heroTitle: "HVAC in Houston. When it's 98° and humid, your phone can't miss.",
    heroDesc:
      "Gulf Coast summers don't play around. When a homeowner's AC quits in Kemah or Clear Lake, they call three companies. The first one to answer books the job. Everyone else gets nothing.",
    leaks: [
      "You're on a roof in League City. Three calls hit voicemail.",
      "A storm rolls in off the bay. Every system in the neighborhood trips at once. You can't triage.",
      "Homeowners search 'AC repair near me' at 11pm and pick whoever answers first.",
    ],
    services: [
      "AC repair & emergency service calls",
      "New AC and heat pump installs",
      "Duct cleaning & sealing for humid coastal homes",
      "Mini-split installs for garages and additions",
      "Preventive maintenance plans (spring + fall tune-ups)",
      "Indoor air quality — dehumidifiers, UV, filtration",
    ],
    googles: ["ac repair near me", "24 hour ac repair houston", "ac not cooling league city", "hvac maintenance kemah"],
    season:
      "May through September is a sprint. Hurricane season stacks power surges and compressor failures on top of it. October is the tune-up window before the first cold front. January freezes drive emergency heat calls.",
    result:
      "One HVAC client went from missing 40% of after-hours calls to booking every single one — the AI answers, qualifies, and drops a booked appointment onto his calendar before he's off the ladder.",
  },
  "plumbing-houston-tx": {
    name: "Plumbing",
    label: "plumbing",
    icon: "Wrench",
    heroTitle: "Plumbing in Houston. A slab leak at 2am is a booked job — if you answer.",
    heroDesc:
      "Water damage doesn't wait for business hours. When a pipe bursts in Friendswood or a water heater dies in Clear Lake, the homeowner calls until someone picks up. Be the one who does.",
    leaks: [
      "You're under a sink in Pasadena. The emergency call two neighborhoods over goes to voicemail.",
      "A cold snap freezes lines across League City. Every phone rings at once and you can only grab a few.",
      "Someone Googles 'emergency plumber near me' at midnight and books whoever texts back first.",
    ],
    services: [
      "Emergency leak & burst pipe repair",
      "Water heater repair & replacement (tank + tankless)",
      "Drain cleaning & sewer line clearing",
      "Slab leak detection & repair",
      "Repipes and fixture installs",
      "Backflow testing & water treatment",
    ],
    googles: ["emergency plumber near me", "water heater replacement houston", "slab leak repair league city", "drain cleaning kemah"],
    season:
      "Winter freezes drive burst-pipe emergencies. Spring storms overload drains and sewers. Summer spikes water heater work. We pre-load follow-ups and storm-season blasts around your real calendar.",
    result:
      "One plumbing client stopped losing after-hours emergencies — the missed-call text back grabbed a 2am slab leak and booked it before he opened his eyes.",
  },
  "roofing-houston-tx": {
    name: "Roofing",
    label: "roofing",
    icon: "Home",
    heroTitle: "Roofing in Houston. After every storm, the fastest responder wins the roof.",
    heroDesc:
      "Gulf Coast wind and hail create a flood of damaged roofs overnight. Homeowners in League City and Kemah call the first company that answers and shows up. Speed is the whole game.",
    leaks: [
      "You're on a tear-off in Webster. Storm-damage calls pile up in voicemail while you work.",
      "A hail event hits and every homeowner calls at once — you can't triage or schedule inspections fast enough.",
      "Someone searches 'roof leak repair near me' after a storm and books whoever responds first.",
    ],
    services: [
      "Storm & hail damage repair",
      "Full roof replacement (shingle, metal, flat)",
      "Roof leak detection & emergency tarping",
      "Insurance claim inspections & documentation",
      "Gutter repair & replacement",
      "Roof maintenance & tune-ups",
    ],
    googles: ["roof leak repair near me", "storm damage roof houston", "roof replacement league city", "roof inspection kemah"],
    season:
      "Hurricane season and spring hail drive the biggest surges. We pre-load storm-response campaigns and rapid follow-ups so you're first to the inbox the moment weather hits.",
    result:
      "One roofing client booked a full week of inspections off a single storm — the AI captured every after-hours call and dropped them straight onto the calendar.",
  },
  "electrical-houston-tx": {
    name: "Electrical",
    label: "electrical",
    icon: "Zap",
    heroTitle: "Electrical in Houston. Panels, generators, and outages — booked before the next call.",
    heroDesc:
      "When the power's out in Clear Lake or a panel's sparking in Pasadena, homeowners want a licensed electrician now. The one who answers and books fast wins the job.",
    leaks: [
      "You're finishing a panel in La Porte. The generator lead across town hits voicemail.",
      "A storm knocks out power and every homeowner calls about generators at once.",
      "Someone Googles 'electrician near me' at night and books whoever texts back first.",
    ],
    services: [
      "Panel upgrades & replacements",
      "Whole-home generator installs",
      "Outlet, switch & lighting work",
      "EV charger installation",
      "Troubleshooting & code corrections",
      "Surge protection & rewiring",
    ],
    googles: ["electrician near me", "panel upgrade houston", "generator install league city", "ev charger installation kemah"],
    season:
      "Hurricane season drives generator and surge work. Summer heat spikes panel loads and AC circuit calls. Holidays bring lighting jobs. We map campaigns to each window.",
    result:
      "One electrical client closed a $9,400 panel + generator job that came straight from the site — the first booked lead he'd ever gotten from a website.",
  },
  "painting-houston-tx": {
    name: "Painting",
    label: "painting",
    icon: "Paintbrush",
    heroTitle: "Painting in Houston. The estimate you follow up on is the job you win.",
    heroDesc:
      "Homeowners in Friendswood and Kemah get three painting quotes and go with whoever stays in touch. Miss the follow-up and you lose the job — even when your work is better.",
    leaks: [
      "You're on a ladder in League City while new estimate requests sit unanswered.",
      "You quote a job, then get busy — the homeowner books the contractor who followed up.",
      "Someone searches 'house painters near me' and picks whoever responds and looks trustworthy online.",
    ],
    services: [
      "Interior & exterior repainting",
      "Cabinet refinishing",
      "Deck & fence staining",
      "Drywall repair & texture",
      "Pressure washing prep",
      "Color consultation & samples",
    ],
    googles: ["house painters near me", "exterior painting houston", "cabinet painting league city", "interior painters kemah"],
    season:
      "Spring and fall are exterior season on the coast. Summer heat pushes interior work. We automate estimate follow-ups so no quote goes cold while you're on the job.",
    result:
      "One painting client stopped losing quotes to slow follow-up — automated estimate reminders recovered jobs that used to go silent.",
  },
  "flooring-houston-tx": {
    name: "Flooring",
    label: "flooring",
    icon: "Layers",
    heroTitle: "Flooring in Houston. Big-ticket estimates that never go cold.",
    heroDesc:
      "Flooring is a considered purchase. Homeowners in Clear Lake compare quotes for weeks. The company that keeps following up — professionally, automatically — closes the sale.",
    leaks: [
      "You're installing in Webster while new leads and measure requests pile up.",
      "A homeowner requests a quote, then goes quiet — and books whoever stayed in touch.",
      "Someone Googles 'flooring installers near me' and picks the business with the most reviews.",
    ],
    services: [
      "Luxury vinyl plank & waterproof flooring",
      "Hardwood install & refinishing",
      "Tile & backsplash installation",
      "Carpet supply & install",
      "Subfloor repair & leveling",
      "Free in-home measure & quote",
    ],
    googles: ["flooring installers near me", "vinyl plank flooring houston", "hardwood refinishing league city", "tile installation kemah"],
    season:
      "Post-storm water damage drives replacement work. Spring and pre-holiday remodels spike installs. We keep long sales cycles warm with automated follow-up.",
    result:
      "One flooring client recovered a stack of stalled estimates — automated follow-ups turned month-old quotes into booked installs.",
  },
  "landscaping-houston-tx": {
    name: "Landscaping",
    label: "landscaping",
    icon: "Sprout",
    heroTitle: "Landscaping in Houston. Recurring routes that compound month over month.",
    heroDesc:
      "Lawn and landscape work is repeat revenue. Homeowners in League City want a crew that answers, shows up, and stays on schedule. Book the route once and it pays all year.",
    leaks: [
      "You're mowing in Kemah while new maintenance leads hit voicemail.",
      "One-time cleanups never get converted into recurring maintenance plans.",
      "Someone searches 'lawn care near me' and books whoever responds first and looks reliable.",
    ],
    services: [
      "Weekly & bi-weekly lawn maintenance",
      "Landscape design & installs",
      "Mulching, beds & seasonal color",
      "Irrigation install & repair",
      "Cleanups & hauling",
      "Tree & shrub trimming",
    ],
    googles: ["lawn care near me", "landscaping houston", "yard cleanup league city", "sprinkler repair kemah"],
    season:
      "Spring green-up and fall cleanup are the big pushes; summer is peak mowing on the coast. We automate the offers that turn one-time jobs into recurring routes.",
    result:
      "One landscaping client added 3 new maintenance routes in a week — roughly $14k a year — just from leads that used to slip away.",
  },
  "cleaning-houston-tx": {
    name: "Cleaning",
    label: "cleaning",
    icon: "SprayCan",
    heroTitle: "Cleaning in Houston. Fast replies fill the calendar — and keep it full.",
    heroDesc:
      "House cleaning is booked by whoever answers fast and feels trustworthy. Homeowners in Friendswood want easy scheduling. Recurring cleans are the goal — and they compound.",
    leaks: [
      "You're mid-clean in Clear Lake while new booking requests go unanswered.",
      "One-time cleans never convert to recurring weekly or bi-weekly plans.",
      "Someone Googles 'house cleaning near me' and books whoever replies and has the best reviews.",
    ],
    services: [
      "Recurring house cleaning (weekly/bi-weekly)",
      "Deep cleans & spring cleaning",
      "Move-in / move-out cleans",
      "Post-construction cleanup",
      "Airbnb & turnover cleaning",
      "Office & commercial cleaning",
    ],
    googles: ["house cleaning near me", "maid service houston", "move out cleaning league city", "deep cleaning kemah"],
    season:
      "Spring cleaning and holiday prep drive spikes; summer moves fuel turnover cleans. We automate rebooking so one clean becomes a recurring plan.",
    result:
      "One cleaning client filled their calendar with recurring clients — automated rebooking turned one-time deep cleans into weekly routes.",
  },
  "moving-houston-tx": {
    name: "Moving",
    label: "moving",
    icon: "Package",
    heroTitle: "Moving in Houston. Book the move before they call the next van.",
    heroDesc:
      "Moving quotes are won on speed and trust. Homeowners in League City call several movers and book whoever answers, quotes fast, and shows real reviews.",
    leaks: [
      "You're loading a truck in Pasadena while new quote requests hit voicemail.",
      "A homeowner requests an estimate, then books whoever responded and confirmed a date first.",
      "Someone searches 'movers near me' and picks the company with fast replies and strong reviews.",
    ],
    services: [
      "Local residential moves",
      "Apartment & condo moves",
      "Packing & unpacking services",
      "Loading / unloading labor only",
      "Furniture & specialty item moving",
      "Storage & short-term holds",
    ],
    googles: ["movers near me", "local moving company houston", "apartment movers league city", "packing services kemah"],
    season:
      "Summer and month-ends are peak moving windows on the coast. We automate fast quotes and date confirmations so leads don't drift to the next van.",
    result:
      "One moving client stopped losing weekend jobs — instant quote replies and text-back locked in dates before competitors called.",
  },
  "pest-control-houston-tx": {
    name: "Pest Control",
    label: "pest control",
    icon: "Bug",
    heroTitle: "Pest Control in Houston. Recurring plans that book themselves.",
    heroDesc:
      "Coastal Texas means year-round pests. Homeowners in Kemah want a responsive company — and recurring quarterly plans turn one call into steady revenue.",
    leaks: [
      "You're treating a home in Webster while new leads go to voicemail.",
      "One-time treatments never convert into recurring quarterly plans.",
      "Someone Googles 'pest control near me' and books whoever answers first and has reviews.",
    ],
    services: [
      "General pest (ants, roaches, spiders)",
      "Termite inspections & treatment",
      "Mosquito control programs",
      "Rodent exclusion & control",
      "Recurring quarterly plans",
      "Commercial pest control",
    ],
    googles: ["pest control near me", "exterminator houston", "termite treatment league city", "mosquito control kemah"],
    season:
      "Warm, humid coast keeps pests active most of the year; spring and summer spike mosquito and ant work. We automate the offers that lock in recurring plans.",
    result:
      "One pest control client booked out through the next week and had to turn work away — the system captured every lead automatically.",
  },
  "solar-houston-tx": {
    name: "Solar",
    label: "solar",
    icon: "Sun",
    heroTitle: "Solar in Houston. Long sales cycles that stay warm until they close.",
    heroDesc:
      "Solar is a months-long decision. Homeowners in Clear Lake compare bids and incentives. The company that follows up consistently — without nagging — wins the install.",
    leaks: [
      "You're on a site survey in League City while new inquiries sit unanswered.",
      "A homeowner requests a quote, then goes quiet for weeks and books whoever stayed in touch.",
      "Someone searches 'solar installers near me' and picks the company with reviews and fast answers.",
    ],
    services: [
      "Residential solar panel installs",
      "Battery backup & storage",
      "System design & savings estimates",
      "Roof + solar coordinated projects",
      "Monitoring & maintenance",
      "Incentive & financing guidance",
    ],
    googles: ["solar installers near me", "solar panels houston", "home battery backup league city", "solar cost kemah"],
    season:
      "Hurricane season and summer bills drive backup and savings interest. We keep long sales cycles warm with automated, no-pressure follow-up.",
    result:
      "One solar client closed deals that used to stall — automated nurturing kept months-long leads engaged until they signed.",
  },
  "tree-service-houston-tx": {
    name: "Tree Service",
    label: "tree service",
    icon: "TreePine",
    heroTitle: "Tree Service in Houston. After the storm, the first crew booked wins.",
    heroDesc:
      "Gulf Coast storms drop limbs and trees overnight. Homeowners in Friendswood need removal fast. The crew that answers and schedules first books the emergency work.",
    leaks: [
      "You're up in a tree in Kemah while emergency removal calls hit voicemail.",
      "A storm drops trees across League City and every phone rings at once.",
      "Someone Googles 'tree removal near me' after a storm and books whoever responds first.",
    ],
    services: [
      "Emergency storm & limb removal",
      "Tree removal & stump grinding",
      "Trimming & canopy thinning",
      "Hazardous tree assessment",
      "Lot clearing",
      "Storm cleanup & hauling",
    ],
    googles: ["tree removal near me", "emergency tree service houston", "stump grinding league city", "tree trimming kemah"],
    season:
      "Hurricane season and spring storms drive emergency removals. We pre-load storm-response campaigns so you're first to the inbox when weather hits.",
    result:
      "One tree service client booked a full week of storm work off a single event — the AI captured every after-hours call instantly.",
  },
  "pressure-washing-houston-tx": {
    name: "Pressure Washing",
    label: "pressure washing",
    icon: "Droplets",
    heroTitle: "Pressure Washing in Houston. Fast quotes turn curb appeal into cash.",
    heroDesc:
      "Coastal humidity means mildew, grime, and green everywhere. Homeowners in League City want a quick quote and a fast date. Answer first and the driveway's yours.",
    leaks: [
      "You're washing a house in Webster while new quote requests go unanswered.",
      "A homeowner asks for a price, then books whoever replied and scheduled first.",
      "Someone searches 'pressure washing near me' and picks the business with reviews and quick answers.",
    ],
    services: [
      "House & siding soft washing",
      "Driveway & concrete cleaning",
      "Roof soft wash (algae removal)",
      "Deck & fence cleaning",
      "Gutter brightening",
      "Commercial & fleet washing",
    ],
    googles: ["pressure washing near me", "house washing houston", "driveway cleaning league city", "roof soft wash kemah"],
    season:
      "Humid spring and summer drive mildew and algae work; pre-holiday curb-appeal jobs spike in fall. We automate fast quotes and rebooking offers.",
    result:
      "One pressure washing client filled weekends solid — instant quote replies converted browsers into booked driveways.",
  },
  "handyman-houston-tx": {
    name: "Handyman",
    label: "handyman",
    icon: "Hammer",
    heroTitle: "Handyman in Houston. The small jobs add up — if you answer them all.",
    heroDesc:
      "Homeowners in Kemah have a punch list and want one reliable person. The handyman who answers, quotes, and shows up books repeat work for years.",
    leaks: [
      "You're mid-repair in Pasadena while new small-job requests hit voicemail.",
      "Little jobs never get followed up — and one-off customers never become repeats.",
      "Someone Googles 'handyman near me' and books whoever answers and looks trustworthy.",
    ],
    services: [
      "Home repairs & odd jobs",
      "Drywall, doors & trim",
      "Fixture & TV mounting",
      "Furniture assembly",
      "Small carpentry & caulking",
      "Honey-do lists & punch-outs",
    ],
    googles: ["handyman near me", "home repair houston", "tv mounting league city", "furniture assembly kemah"],
    season:
      "Spring and pre-holiday to-do lists drive the biggest waves. We automate follow-up so one-off jobs turn into a repeat customer base.",
    result:
      "One handyman client built a repeat customer base — automated follow-ups turned single jobs into regulars who call first.",
  },
  "remodeling-houston-tx": {
    name: "Remodeling",
    label: "remodeling",
    icon: "PencilRuler",
    heroTitle: "Remodeling in Houston. High-ticket projects go to whoever follows up right.",
    heroDesc:
      "Kitchen and bath remodels are big, considered decisions. Homeowners in League City and Clear Lake gather bids for weeks. The contractor who answers, quotes clearly, and stays in touch wins the project.",
    leaks: [
      "You're on a build in Kemah while new project inquiries sit in voicemail.",
      "A homeowner requests a design consult, then books whoever followed up with a clear next step.",
      "Someone Googles 'kitchen remodel near me' and picks the company with a portfolio and strong reviews.",
    ],
    services: [
      "Kitchen remodels & renovations",
      "Bathroom remodels",
      "Whole-home renovations",
      "Room additions & expansions",
      "Custom cabinetry & countertops",
      "Design-build & 3D planning",
    ],
    googles: ["kitchen remodel near me", "bathroom remodel houston", "home renovation league city", "room addition kemah"],
    season:
      "Homeowners plan remodels around the holidays and tax-return season, with a spring-to-summer build push on the coast. We keep long, high-value sales cycles warm with automated, professional follow-up.",
    result:
      "One remodeling client closed a full kitchen renovation that had stalled for a month — automated follow-up and a clean portfolio page brought the homeowner back to sign.",
  },
  "fence-houston-tx": {
    name: "Fence",
    label: "fence",
    icon: "Fence",
    heroTitle: "Fence in Houston. Big estimates that stay warm until they close.",
    heroDesc:
      "Fencing is a considered buy. Homeowners in League City gather quotes and take their time. The contractor who follows up consistently closes the install.",
    leaks: [
      "You're setting posts in Webster while new estimate requests go unanswered.",
      "A homeowner requests a quote, then books whoever stayed in touch.",
      "Someone searches 'fence installers near me' and picks the company with reviews and fast replies.",
    ],
    services: [
      "Wood privacy fence install",
      "Chain-link & metal fencing",
      "Fence repair & storm damage",
      "Gates & automatic openers",
      "Deck & pergola builds",
      "Free on-site estimates",
    ],
    googles: ["fence installers near me", "privacy fence houston", "fence repair league city", "gate installation kemah"],
    season:
      "Storm season drives repair and replacement; spring and fall spike new installs. We automate estimate follow-ups so quotes don't go cold.",
    result:
      "One fence client recovered stalled bids — automated follow-ups turned old estimates into booked installs.",
  },
  "exterior-services-houston-tx": {
    name: "Exterior Services",
    label: "exterior",
    icon: "Building2",
    heroTitle: "Exterior Services in Houston. Every curb-appeal job, captured and booked.",
    heroDesc:
      "Siding, soffit, gutters, and more — homeowners in Clear Lake want one dependable crew. The company that answers fast and follows up wins the whole exterior.",
    leaks: [
      "You're on a job in La Porte while new exterior leads hit voicemail.",
      "Estimates go quiet and homeowners book whoever kept in touch.",
      "Someone Googles 'siding contractor near me' and picks the business with reviews and fast answers.",
    ],
    services: [
      "Siding repair & replacement",
      "Soffit, fascia & trim",
      "Gutter install & repair",
      "Exterior painting prep",
      "Storm & wind damage repair",
      "Window & door exteriors",
    ],
    googles: ["siding contractor near me", "gutter installation houston", "exterior repair league city", "soffit fascia kemah"],
    season:
      "Hurricane and hail season drive repairs; spring and fall spike curb-appeal upgrades. We pre-load storm-response and follow-up campaigns around your calendar.",
    result:
      "One exterior services client booked a run of siding and gutter jobs off storm leads — the system captured every call automatically.",
  },
};
